/**
 * Add a tenant.
 *
 * This form is the payoff for the whole architecture: submitting it creates an
 * academy without a commit, a Tutor render, or a deploy. Today the same outcome
 * needs an edit to multisite-sites.yml and a kubectl exec into the LMS pod.
 *
 * Deliberately short. Only the fields with no safe default are asked for; branding,
 * copy and flags are edited afterwards, on a tenant that already exists and can be
 * looked at. A long creation form is how you end up with half-configured tenants.
 */

import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Banner, Button, Card, Field } from '../../shared/ui';
import { ApiError } from '../../shared/lib/openedx';
import { tenantsApi, validateDraft, type Tenant, type TenantDraft } from '../api/tenants';

export default function TenantCreate() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const [name, setName] = useState('');
  const [slug, setSlug] = useState('');
  const [slugEdited, setSlugEdited] = useState(false);
  const [site, setSite] = useState('');
  const [orgs, setOrgs] = useState('');
  const [errors, setErrors] = useState<Record<string, string>>({});

  const create = useMutation<Tenant, unknown, TenantDraft>({
    mutationFn: (draft: TenantDraft) => tenantsApi.create(draft),
    onSuccess: (tenant) => {
      queryClient.invalidateQueries({ queryKey: ['tenants'] });
      navigate(`/tenants/${tenant.slug}?created=1`);
    },
  });

  /** Suggest the slug from the name, but stop the moment it's edited by hand —
      silently overwriting a deliberate slug is worse than a wrong suggestion. */
  function onNameChange(value: string) {
    setName(value);
    if (!slugEdited) {
      setSlug(
        value
          .toLowerCase()
          .replace(/[^a-z0-9]+/g, '-')
          .replace(/^-+|-+$/g, '')
          .slice(0, 40),
      );
    }
  }

  function submit(event: React.FormEvent) {
    event.preventDefault();

    const draft: TenantDraft = {
      slug,
      name: name.trim(),
      status: 'planned',
      domains: { site: site.trim().toLowerCase().replace(/^https?:\/\//, '').replace(/\/$/, '') },
      orgs: orgs
        .split(',')
        .map((org) => org.trim().toUpperCase())
        .filter(Boolean),
    };

    const found = validateDraft(draft);
    setErrors(found);
    if (Object.keys(found).length > 0) return;

    create.mutate(draft);
  }

  return (
    <div className="console__stack console__stack--narrow">
      <nav className="console__crumbs">
        <Link to="/tenants">Tenants</Link> <span aria-hidden="true">/</span> Add tenant
      </nav>

      <Card title="Add a tenant">
        <form onSubmit={submit} noValidate className="console__form">
          <Field
            label="Academy name"
            name="name"
            value={name}
            onChange={(e) => onNameChange(e.target.value)}
            error={errors.name}
            hint="Shown to learners in the header, on certificates and in emails."
            required
          />

          <Field
            label="Identifier"
            name="slug"
            value={slug}
            onChange={(e) => {
              setSlugEdited(true);
              setSlug(e.target.value);
            }}
            error={errors.slug}
            hint="Permanent. Used in URLs and logs, and never reused once taken."
            required
          />

          <Field
            label="Domain"
            name="site"
            value={site}
            onChange={(e) => setSite(e.target.value)}
            error={errors.site}
            hint="The address learners visit, e.g. academy.example.org. No https:// needed."
            required
          />

          <Field
            label="Organisation codes"
            name="orgs"
            value={orgs}
            onChange={(e) => setOrgs(e.target.value)}
            error={errors.orgs}
            hint="Comma separated, uppercase. Decides which courses appear in this catalogue."
            required
          />

          <Banner tone="info">
            The tenant is created as <strong>planned</strong>, so the domain is registered but
            nothing is served yet. Review the branding, then set it to active when you want
            learners to reach it.
          </Banner>

          {create.error ? <CreateError error={create.error} /> : null}

          <div className="console__form-actions">
            <Button type="submit" variant="primary" busy={create.isPending}>
              Create tenant
            </Button>
            <Link to="/tenants" className="ui-btn" data-variant="ghost">
              Cancel
            </Link>
          </div>
        </form>
      </Card>
    </div>
  );
}

/** The server's own sentence, when it sent one. `{"errors": [...]}` or `{"detail": "..."}`. */
function serverDetail(body: unknown): string | null {
  if (body && typeof body === 'object') {
    const b = body as { errors?: unknown; detail?: unknown };
    if (Array.isArray(b.errors) && b.errors.length > 0) return b.errors.map(String).join(' ');
    if (typeof b.detail === 'string' && b.detail.trim()) return b.detail;
  }
  return null;
}

function formatServerErrors(body: unknown): string {
  return serverDetail(body) ?? 'Check the domain and organisation codes.';
}

/** Turn API failures into something an operator can act on. */
function CreateError({ error }: { error: unknown }) {
  if (error instanceof ApiError) {
    if (error.status === 409) {
      // The server says WHICH of the two collided and with whom, so show its sentence
      // rather than this page's guess. This banner used to promise a domain check the
      // API did not perform — a duplicate domain returned 201 and quietly took the
      // other academy over — so the generic wording is the fallback now, not the answer.
      return (
        <Banner tone="danger">
          <strong>{serverDetail(error.body) ?? 'That identifier or domain is already taken.'}</strong>{' '}
          Each must be unique across the platform — check the tenant list, including
          suspended ones.
        </Banner>
      );
    }
    if (error.status === 422 || error.status === 400) {
      return (
        <Banner tone="danger">
          <strong>The tenancy service rejected these values.</strong>{' '}
          {/* The server returns {"errors": [...]} or {"detail": "..."}; show whichever
              is present rather than a generic message, since these are actionable. */}
          {formatServerErrors(error.body)}
        </Banner>
      );
    }
    if (error.status === 403) {
      return (
        <Banner tone="danger">
          <strong>You are not allowed to create tenants.</strong> This needs platform
          administrator access.
        </Banner>
      );
    }
  }

  return (
    <Banner tone="danger">
      <strong>The tenant was not created.</strong> The tenancy service did not respond.
      Nothing was saved, so it is safe to try again.
    </Banner>
  );
}
